/**
 * I passi del tutorial guidato dello Smistamento Ordini.
 *
 * Un preparativo solo: il menu dei tag, che si apre dalla chip nella colonna Tag.
 * `stopDispatchOrdersTour` lo richiude a fine giro.
 *
 * Qui i bersagli non possono essere semplici selettori: la pagina tiene in piedi
 * una tabella per ogni tag e nasconde con `v-show` quelle dei tab non attivi,
 * quindi `querySelector` pescherebbe quasi sempre la tabella sbagliata - quella
 * del primo tab, anche quando l'utente sta guardando il terzo. Ogni bersaglio di
 * riga passa percio' da `firstVisible`, che scarta quelli senza area e trova
 * quello davvero a schermo.
 */
const SCOPE = '[data-tour-scope="dispatch-orders"]';

const TAG_LIST = '[data-tour="tag-list"]';

/**
 * Vero quando l'elemento si vede davvero.
 *
 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
 * Vuetify gli toglie subito `menuable__content__active`. Senza questo controllo
 * un menu in chiusura sembrerebbe ancora aperto e il click di pulizia, invece di
 * chiuderlo, lo riaprirebbe.
 */
function isShown(element) {
	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/** Il primo elemento che corrisponde e si vede, oppure `null`. */
function firstVisible(selector) {
	return [...document.querySelectorAll(selector)].find(isShown) || null;
}

function isVisible(selector) {
	return firstVisible(selector) !== null;
}

/** Bersaglio dentro la pagina, preso fra quelli visibili. */
const at = name => () => firstVisible(`${SCOPE} [data-tour="${name}"]`);

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(selector, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(selector) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

/** Apre il menu dei tag dalla chip della prima riga a schermo. */
async function openTagMenu() {
	await afterPendingClose();

	if (isVisible(TAG_LIST)) {
		return;
	}

	const chip = firstVisible(`${SCOPE} [data-tour="tag"]`);

	// Nessuna riga: non c'e' niente da aprire e non ha senso aspettare.
	if (!chip) {
		return;
	}

	chip.click();
	await waitFor(TAG_LIST, true);
}

async function closeTagMenu() {
	await afterPendingClose();

	if (!isVisible(TAG_LIST)) {
		return;
	}

	const chip = firstVisible(`${SCOPE} [data-tour="tag"]`);

	// L'attivatore fa da interruttore: il secondo click richiude.
	if (chip) {
		chip.click();
		await waitFor(TAG_LIST, false);
	}
}

/** Richiude il menu dei tag a fine giro. */
export function stopDispatchOrdersTour() {
	return closeTagMenu();
}

export function createDispatchOrdersTourSteps() {
	return [
		{
			body: "Questa pagina serve a decidere cosa si spedisce e in che ordine. Non e' l'elenco ordini: qui ci sono solo gli ordini ancora da lavorare, divisi in colonne di lavoro - i tag - che ti sei configurato tu. Da ogni riga puoi avviare la preparazione fisica del pacco, cioe' la packing list.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Ogni pulsante e' un tag, nell'ordine che hai stabilito in Impostazioni, e il numero fra parentesi e' quanti ordini ci sono dentro. L'ultimo, Senza tag, e' la coda di chi non e' ancora stato smistato - ed e' l'unico che nasconde gli ordini con una packing list gia' aperta, perche' quelli sono gia' in mano a qualcuno. Negli altri tab, invece, un ordine in preparazione resta visibile.",
			before: closeTagMenu,
			key: 'tabs',
			placement: 'bottom',
			target: at('tabs'),
			title: 'I tab: le tue colonne di lavoro',
		},
		{
			body: "Cerca un ordine per numero o un cliente per nome/cognome: bastano tre caratteri e la board si aggiorna da sola, senza bisogno di premere Invio.",
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare un ordine o un cliente',
		},
		{
			body: 'Senza filtro data la board mostra gli ordini degli ultimi trenta giorni - lo dice la scritta in alto a destra. Scegliendo un intervallo qui quella finestra viene sostituita, non ristretta: con due date prendi il periodo chiuso, con una sola tutto da quel giorno in poi.',
			key: 'date',
			placement: 'bottom',
			target: at('date'),
			title: 'La finestra temporale',
		},
		{
			body: "Platform limita la board a uno o piu' canali di vendita. L'elenco non e' fisso: contiene solo le integrazioni davvero attive sul tuo profilo, piu' Store per gli ordini nati sul tuo sito.",
			key: 'platform',
			placement: 'bottom',
			target: at('platform'),
			title: 'Filtrare per canale',
		},
		{
			body: "Qui scegli quali stati vuoi vedere in board: parte gia' pieno con i tre stati di un ordine ancora da seguire - In preparazione, Spedito, Spedito parzialmente - ed e' il motivo per cui vedi solo ordini vivi. Aggiungendo uno stato come Completato o Reso, quegli ordini iniziano a comparire.",
			key: 'status',
			placement: 'bottom',
			target: at('status'),
			title: 'Filtrare per stato',
		},
		{
			body: "Riporta i filtri come li trovi entrando: nessuna ricerca, nessun canale, nessuna data, e gli stati di lavorazione standard (In preparazione, Spedito, Spedito parzialmente). Resta spento finche' non ti sei davvero discostato da quel punto di partenza, cosi' si vede a colpo d'occhio se la board che stai guardando e' filtrata o no. Accanto, la freccia circolare rilegge gli stessi dati dal server.",
			key: 'reset',
			placement: 'left',
			target: at('reset'),
			title: 'Azzerare i filtri',
		},
		{
			body: "Una riga per ordine, con data, canale, numero, cliente, stato, pagamento, spedizione e totale. Lo Stato ha il colore che hai scelto in Impostazioni, lo stesso dell'elenco ordini classico, cosi' l'occhio ritrova le stesse convenzioni. Le ultime due colonne - Tag e Packing List - sono quelle che esistono solo qui, e sono il motivo per cui questa pagina c'e'.",
			key: 'table',
			placement: 'bottom',
			/*
			 * L'intestazione, non la tabella intera: con venticinque ordini la tabella
			 * e' piu' alta dello schermo e il ritaglio coprirebbe tutto il visibile,
			 * facendo sembrare sparito il velo.
			 */
			target: () => {
				const table = firstVisible(`${SCOPE} [data-tour="table"]`);

				return table ? table.querySelector('thead') : null;
			},
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "Il piu' apre la riga e mostra cosa c'e' dentro l'ordine: foto, sku, titolo, quantita' e prezzo di ogni prodotto. Serve a capire in un attimo se un ordine e' un pezzo solo o un carrello lungo - cioe' quanto lavoro sara' prepararlo - senza aprire nulla.",
			key: 'expand',
			placement: 'right',
			target: at('expand'),
			timeout: 250,
			title: 'Vedere cosa contiene un ordine',
		},
		{
			body: "La chip nella colonna Tag dice in quale colonna si trova l'ordine, e cliccandola la si cambia: e' questo il gesto dello smistamento. L'ordine sparisce dal tab di partenza e compare in cima a quello di arrivo, subito, senza ricaricare la pagina.",
			before: closeTagMenu,
			key: 'tag',
			placement: 'left',
			target: at('tag'),
			timeout: 250,
			title: 'Spostare un ordine',
		},
		{
			body: "Il menu elenca tutti i tag configurati piu' la voce Nessun tag, che rimanda l'ordine nella coda Senza tag. La scelta viene salvata sull'ordine ed e' la stessa etichetta che si vede nell'elenco ordini: non e' un'annotazione buona solo per questa schermata.",
			before: openTagMenu,
			key: 'tag-list',
			placement: 'left',
			target: () => firstVisible(TAG_LIST),
			timeout: 250,
			title: 'Scegliere il tag',
		},
		{
			body: "Avvia Packing List porta alla pagina di avvio con l'ordine di questa riga gia' scelto: se il badge e' gia' stato passato la preparazione parte subito, altrimenti appena lo passi. Poi si apre la pagina di lavorazione, e Indietro riporta qui con gli stessi filtri e la stessa tab. Quando una preparazione e' gia' in corso, al suo posto compare chi la sta facendo e il pulsante Apri Packing List per riprenderla.",
			before: closeTagMenu,
			key: 'packing',
			placement: 'left',
			target: at('packing'),
			timeout: 250,
			title: 'Avviare e seguire la preparazione',
		},
		{
			body: 'Un avvertimento sulla paginazione: la pagina e il numero di righe valgono per tutti i tab insieme, non per quello che stai guardando. Passando a pagina 2 ci vanno tutte le colonne, quindi conviene pensarla come una posizione unica della board piuttosto che come lo scorrimento di una tabella sola.',
			key: 'pagination',
			placement: 'top',
			target: () => firstVisible(`${SCOPE} .v-data-footer`),
			timeout: 250,
			title: "La paginazione e' condivisa",
		},
	];
}
